import {
  Box,
  Card,
  CardBody,
  Divider,
  Flex,
  IconButton,
  Image,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import React, { useState } from 'react';
import { FaPhone } from 'react-icons/fa';

import { CallCenter } from '../../../../../assets/Images';
import { WebCloserName } from '../../../../../constants/misc';
import { useDealAdvisorsQuery } from '../../../../../gql/generated/graphql';
import getSearchPhoneNumbers from '../../../../../utils/getSearchPhoneNumbers';
import { DealStateEnum } from '../../../../../utils/types/deal';
import { NEW_DEAL_VALUE, dashboardDealInfoAtom } from '../../../RootDashboard';
import { DealStateMap, DealWithFarthestState } from '../../../utils';

type Advisor = {
  role: string;
  name: string;
  photoUrl?: string | null;
};

type Props = { deal: DealWithFarthestState };

const AdvisorPhoto = ({ photoUrl, name }: { photoUrl?: string | null; name: string }) => {
  const [errored, setErrored] = useState(false);
  const showFallback = !photoUrl || errored;
  return (
    <Image
      src={showFallback ? CallCenter : (photoUrl as string)}
      alt={showFallback ? '' : `Photo of ${name}`}
      boxSize="44px"
      borderRadius="full"
      objectFit="cover"
      onError={() => setErrored(true)}
    />
  );
};

const AdvisorRow = ({ advisor, phoneNumber }: { advisor: Advisor; phoneNumber: string }) => (
  <Flex bgColor="white" borderRadius="16px" p={3} align="center" gap={3} justify="space-between">
    <Flex align="center" gap={3} flex={1} minW={0}>
      <AdvisorPhoto photoUrl={advisor.photoUrl} name={advisor.name} />
      <Box minW={0}>
        <Text
          fontSize="2xs"
          fontWeight="700"
          color="gray.500"
          letterSpacing="wider"
          textTransform="uppercase"
        >
          {advisor.role}
        </Text>
        <Text fontSize="md" fontWeight="700" color="leaseEndBlue" isTruncated>
          {advisor.name}
        </Text>
      </Box>
    </Flex>
    <IconButton
      as={Link}
      href={`tel:${phoneNumber}`}
      aria-label={`Call ${advisor.name}`}
      icon={<FaPhone />}
      bgColor="successGreen"
      color="white"
      borderRadius="full"
      size="md"
      minW="44px"
      h="44px"
      _hover={{ bgColor: 'green.500' }}
    />
  </Flex>
);

const LeaseEndTeamCard = ({ deal }: Props) => {
  const dealId = useAtomValue(dashboardDealInfoAtom);
  const { data } = useDealAdvisorsQuery({
    variables: {
      dealId: dealId?.id === NEW_DEAL_VALUE.id ? undefined : dealId?.id,
    },
  });
  const phoneNumber = getSearchPhoneNumbers();

  const farthestValue = DealStateMap[deal?.farthestState as DealStateEnum];
  const showFunding = farthestValue >= DealStateMap[DealStateEnum.Funded];
  const showTitle = farthestValue >= DealStateMap[DealStateEnum.WaitingForTitle];

  const closer = data?.dealAdvisors?.closer;
  const fundingClerk = data?.dealAdvisors?.funding_clerk;
  const titleClerk = data?.dealAdvisors?.title_clerk;

  const advisors: Advisor[] = [];
  if (closer?.name && closer.name !== WebCloserName) {
    advisors.push({ role: 'Financial Specialist', name: closer.name, photoUrl: closer.photo_url });
  }
  if (showFunding && fundingClerk?.name) {
    advisors.push({
      role: 'Funding Advisor',
      name: fundingClerk.name,
      photoUrl: fundingClerk.photo_url,
    });
  }
  if (showTitle && titleClerk?.name) {
    advisors.push({
      role: 'Title Advisor',
      name: titleClerk.name,
      photoUrl: titleClerk.photo_url,
    });
  }

  if (advisors.length === 0) return null;

  return (
    <Card borderRadius="24px" p={2}>
      <Box px={2} py={2}>
        <Text
          fontSize={{ base: 'lg', md: 'xl' }}
          fontWeight="800"
          color="leaseEndBlue"
          letterSpacing="wider"
        >
          Your Lease End Team
        </Text>
        <Text fontSize="md" color="gray.600" mt={1}>
          Questions? Your team is here to help.
        </Text>
      </Box>
      <CardBody p={4} bgColor="grayCoolLight" borderRadius="16px">
        <Stack spacing={3}>
          {advisors.map((advisor) => (
            <AdvisorRow key={advisor.role} advisor={advisor} phoneNumber={phoneNumber} />
          ))}
        </Stack>
        <Divider my={4} borderColor="gray.300" />
        <Text fontSize="sm" color="gray.600" textAlign="center">
          All calls go to{' '}
          <Link href={`tel:${phoneNumber}`} color="leaseEndBlue" fontWeight="700">
            {phoneNumber}
          </Link>
        </Text>
      </CardBody>
    </Card>
  );
};

export default LeaseEndTeamCard;
